"use client";

import { useTranslations } from "next-intl";
import { MessageCircle, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface SupportLauncherProps {
  unreadCount: number;
  open: boolean;
  onClick: () => void;
}

export function SupportLauncher({
  unreadCount,
  open,
  onClick,
}: SupportLauncherProps) {
  const t = useTranslations("support.launcher");

  return (
    <button
      type="button"
      aria-label={open ? t("close") : t("open")}
      aria-expanded={open}
      onClick={onClick}
      // Position is purely CSS-driven so there's no SSR/CSR mismatch:
      //   • Mobile (<md): just above the BottomNavBar (~4rem) + safe-area inset.
      //   • Desktop (≥md): standard bottom-right.
      // Inline style covers the safe area; md: breakpoint resets it.
      style={{ bottom: "calc(5rem + var(--loc-safe-area-inset-bottom))" }}
      className={cn(
        // `support-launcher` is the hook that globals.css uses to hide the
        // FAB whenever any Radix/Vaul modal sets `data-scroll-locked` on body
        // (cart, voucher, etc.) so it doesn't float on top of the drawer.
        // Sits above the support panel (z-50) so it's always tappable as the toggle.
        "support-launcher",
        "fixed right-4 z-60 flex items-center justify-center",
        "bg-orange-500 text-white shadow-lg transition-all hover:bg-orange-600",
        "focus:outline-none focus:ring-4 focus:ring-orange-200",
        // Fixed square (56px mobile / 64px desktop) in both states so the
        // toggle doesn't shift size or position. Closed shows icon over label;
        // open collapses to just the X (a label would contradict "close").
        "h-14 w-14 rounded-3xl md:h-16 md:w-16",
        open ? "" : "flex-col gap-0.5 px-1 font-semibold",
        // Override the mobile bottom on desktop (Tailwind's important-style
        // utility wins over inline style with `!`).
        "md:bottom-6! md:right-6",
      )}
    >
      {open ? (
        <X className="h-5 w-5 md:h-6 md:w-6" />
      ) : (
        <>
          <MessageCircle className="h-5 w-5 shrink-0 md:h-6 md:w-6" />
          <span className="text-center text-[11px] leading-tight">
            {t("label")}
          </span>
        </>
      )}
      {!open && unreadCount > 0 && (
        <span
          aria-hidden
          className="absolute -right-1 -top-1 inline-flex min-w-4.5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-semibold leading-4.5 text-white"
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </button>
  );
}
